/**
 * useMessages Hook
 * 
 * Manages messages for a conversation with real-time updates.
 * 
 * Features:
 * - In-memory cache for instant load (synchronous, 0ms)
 * - IndexedDB cache fallback (fast async)
 * - Real-time Firestore subscription for live updates
 * - Background sync - cached data shown first, then updated silently
 * - Pagination for older messages
 * - Message grouping logic
 * - Offline support (read cached messages)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  subscribeToMessages,
  getMessages,
  markConversationAsRead,
  isMessageVisible,
  clearUnreadCount,
  unarchiveConversation
} from '@/lib/firestore';
import { 
  getCachedMessages, 
  cacheMessages,
  getMemoryCachedMessages,
  setMemoryCachedMessages
} from '@/lib/messageCache';
import { shouldGroupMessages, isDifferentDay } from '../utils/timeFormat';

/**
 * Convert a message's createdAt to epoch millis for sorting.
 * Accepts a Firestore Timestamp, a JS Date, an ISO string, or null.
 *
 * A missing/unresolved timestamp means the message was just created locally
 * (a pending serverTimestamp that hasn't been acknowledged yet) — i.e. it is the
 * NEWEST message. We return +Infinity for that case so it sorts to the bottom,
 * never to 1970 (which previously yanked just-sent messages to the top of the
 * thread). Using a fixed sentinel (rather than Date.now()) keeps the comparator
 * deterministic across the many calls a sort makes.
 */
function messageTimeMillis(createdAt) {
  if (createdAt?.toDate) return createdAt.toDate().getTime();
  if (createdAt != null) {
    const t = new Date(createdAt).getTime();
    if (!Number.isNaN(t)) return t;
  }
  return Number.POSITIVE_INFINITY;
}

export function useMessages(conversationId, currentUserId, participants = []) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isFromCache, setIsFromCache] = useState(false);
  
  const lastDocRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const hasSyncedRef = useRef(false);
  const currentConversationRef = useRef(null);
  const processedMessageIdsRef = useRef(new Set());
  // COM-B45c (owner ruling 94 §21B): an archived chat comes back only when a
  // genuinely NEW message arrives — never on a read receipt, reaction, edit or
  // the initial load. Track the ids every earlier snapshot already carried.
  const seenMessageIdsRef = useRef(new Set());
  const firstSnapshotSeenRef = useRef(false);
  const participantsRef = useRef(participants);
  
  // Keep participants ref up to date
  participantsRef.current = participants;

  // Single effect that handles everything: cache load, state reset, and Firestore subscription
  useEffect(() => {
    // Early exit if no conversation
    if (!conversationId || !currentUserId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    let isMounted = true;
    
    // Track current conversation to avoid stale updates
    currentConversationRef.current = conversationId;

    // Reset refs for new conversation
    lastDocRef.current = null;
    hasSyncedRef.current = false;
    processedMessageIdsRef.current = new Set();
    seenMessageIdsRef.current = new Set();
    firstSnapshotSeenRef.current = false;

    // STEP 1: Check in-memory cache FIRST (synchronous, truly instant)
    const memoryCached = getMemoryCachedMessages(conversationId);
    if (memoryCached && memoryCached.length > 0) {
      const visibleMemory = memoryCached.filter(msg => 
        isMessageVisible(msg, currentUserId)
      );
      if (visibleMemory.length > 0) {
        // Show immediately - no async, no loading state
        setMessages(processMessagesWithGrouping(visibleMemory, currentUserId));
        setIsFromCache(true);
        setLoading(false);
      }
    }

    const initConversation = async () => {
      // STEP 2: If no memory cache, try IndexedDB (async but fast)
      if (!memoryCached || memoryCached.length === 0) {
        try {
          const cachedMessages = await getCachedMessages(conversationId);
          
          if (!isMounted || currentConversationRef.current !== conversationId) return;
          
          if (cachedMessages.length > 0) {
            const visibleCached = cachedMessages.filter(msg => 
              isMessageVisible(msg, currentUserId)
            );
            
            if (visibleCached.length > 0) {
              setMessages(processMessagesWithGrouping(visibleCached, currentUserId));
              setIsFromCache(true);
              setLoading(false);
              // Also store in memory cache for next time
              setMemoryCachedMessages(conversationId, cachedMessages);
            }
          }
        } catch (err) {
          console.error('[useMessages] Cache read error:', err);
        }
      }

      // If still no cached data, show loading state
      if (!memoryCached || memoryCached.length === 0) {
        // Check if we got data from IndexedDB above
        // If not, set loading state
        setMessages(prev => {
          if (prev.length === 0) {
            setLoading(true);
          }
          return prev;
        });
      }
      
      // Reset other state
      setLoadingMore(false);
      setHasMore(true);
      setError(null);

      // STEP 3: Subscribe to Firestore for real-time updates
      unsubscribeRef.current = subscribeToMessages(
        conversationId,
        (fetchedMessages) => {
          // Guard: ignore stale callbacks from a previous conversation subscription
          if (currentConversationRef.current !== conversationId) return;
          if (!isMounted || currentConversationRef.current !== conversationId) return;

          const visibleMessages = fetchedMessages.filter(msg => 
            isMessageVisible(msg, currentUserId)
          );
          
          setMessages(prev => {
            if (!hasSyncedRef.current) {
              hasSyncedRef.current = true;
              setIsFromCache(false);
              
              // Update both caches
              setMemoryCachedMessages(conversationId, visibleMessages);
              cacheMessages(conversationId, visibleMessages).catch(err => {
                console.error('[useMessages] Cache write error:', err);
              });
              
              return processMessagesWithGrouping(visibleMessages, currentUserId);
            }
            
            const realTimeMessageMap = new Map(
              visibleMessages.map(msg => [msg.id, msg])
            );
            
            const olderMessages = prev
              .filter(msg => !realTimeMessageMap.has(msg.id))
              .filter(msg => isMessageVisible(msg, currentUserId));
            
            const allMessages = [...olderMessages, ...visibleMessages];
            
            allMessages.sort((a, b) => messageTimeMillis(a.createdAt) - messageTimeMillis(b.createdAt));
            
            // Update both caches
            setMemoryCachedMessages(conversationId, allMessages);
            cacheMessages(conversationId, allMessages).catch(err => {
              console.error('[useMessages] Cache update error:', err);
            });
            
            return processMessagesWithGrouping(allMessages, currentUserId);
          });
          
          setLoading(false);
          
          // Clear unread count immediately when viewing this conversation
          // This ensures no badge shows even if a message arrives while viewing
          clearUnreadCount(currentUserId, conversationId).catch(() => {
            // Silently ignore - not critical
          });
          
          // Auto-unarchive ONLY on a genuinely new message (COM-B45c). The first
          // snapshot is the initial load, not an arrival; later snapshots fire for
          // read receipts, reactions, edits and deletes too, which must not bring
          // an archived chat back. A message id no earlier snapshot carried is the
          // only trigger — that covers messages from others and the user's own
          // send from inside an archived thread (the server un-archives recipients
          // on send, not the sender).
          const isFirstSnapshot = !firstSnapshotSeenRef.current;
          const arrivedNow = visibleMessages.some(msg => !seenMessageIdsRef.current.has(msg.id));
          visibleMessages.forEach(msg => seenMessageIdsRef.current.add(msg.id));
          firstSnapshotSeenRef.current = true;
          if (!isFirstSnapshot && arrivedNow) {
            unarchiveConversation(currentUserId, conversationId).catch(() => {
              // Silently ignore - not critical
            });
          }
          
          // Send read receipts for new messages from other users
          // This ensures the sender sees "Read" immediately when recipient has conversation open
          const newUnreadFromOthers = visibleMessages.filter(msg => {
            // Message from someone else (not the current user)
            const isFromOther = msg.senderId !== currentUserId;
            // Not already processed (avoid duplicate read receipts)
            const notProcessed = !processedMessageIdsRef.current.has(msg.id);
            // Not already read by current user
            const notReadByMe = !msg.readBy?.includes(currentUserId);
            return isFromOther && notProcessed && notReadByMe;
          });
          
          if (newUnreadFromOthers.length > 0 && participantsRef.current.length > 0) {
            const messageIds = newUnreadFromOthers.map(m => m.id);
            
            // Mark these messages as processed to avoid duplicate receipts
            messageIds.forEach(id => processedMessageIdsRef.current.add(id));
            
            // (R28: the WebSocket read-receipt dispatch was removed — the server never
            //  handled it; read state is written to Firestore by markConversationAsRead.)
            
            // Also mark these messages as read in Firestore
            markConversationAsRead(conversationId, currentUserId).catch(console.error);
          }
        },
        100
      );

      markConversationAsRead(conversationId, currentUserId).catch(console.error);
    };

    initConversation();

    return () => {
      isMounted = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [conversationId, currentUserId]);

  // Listen for incoming read receipts via WebSocket to update message status in real-time
  // (R28: `ws_read_receipt` listener removed — the server never emitted `message_read`.)

  // Load more messages (for infinite scroll)
  const loadMoreMessages = useCallback(async () => {
    if (!conversationId || !hasMore || loading || loadingMore) return;

    setLoadingMore(true);
    
    try {
      const result = await getMessages(conversationId, lastDocRef.current);
      
      if (result.messages.length > 0) {
        lastDocRef.current = result.lastDoc;
        setHasMore(result.hasMore);
        
        setMessages(prev => {
          const allMessages = [...result.messages, ...prev];
          
          const seenIds = new Set();
          const uniqueMessages = allMessages.filter(msg => {
            if (seenIds.has(msg.id)) return false;
            seenIds.add(msg.id);
            return true;
          });
          
          uniqueMessages.sort((a, b) => messageTimeMillis(a.createdAt) - messageTimeMillis(b.createdAt));
          
          return processMessagesWithGrouping(uniqueMessages, currentUserId);
        });
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error loading more messages:', err);
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  }, [conversationId, hasMore, loading, loadingMore, currentUserId]);

  return {
    messages,
    loading,
    loadingMore,
    error,
    hasMore,
    isFromCache,
    loadMoreMessages
  };
}

/**
 * Process messages to add grouping and date separator info
 */
function processMessagesWithGrouping(messages, currentUserId) {
  if (!messages || messages.length === 0) return [];

  return messages.map((message, index) => {
    const prevMessage = index > 0 ? messages[index - 1] : null;
    const nextMessage = index < messages.length - 1 ? messages[index + 1] : null;

    const showDateSeparator = !prevMessage || isDifferentDay(prevMessage.createdAt, message.createdAt);

    const isFirstInGroup = !prevMessage || 
      !shouldGroupMessages(prevMessage, message) || 
      showDateSeparator;

    const isLastInGroup = !nextMessage || 
      !shouldGroupMessages(message, nextMessage) ||
      isDifferentDay(message.createdAt, nextMessage.createdAt);

    const isOwnMessage = message.senderId === currentUserId;

    return {
      ...message,
      isOwnMessage,
      showDateSeparator,
      isFirstInGroup,
      isLastInGroup,
      showSenderName: isFirstInGroup && !isOwnMessage
    };
  });
}
